import { useTranslate } from '@refinedev/core';
import { App as AntdApp, Input, Typography } from 'antd';
import { useEffect, useState } from 'react';
import { SectionDrawer } from '../../components/ToolSection';
import { retireInputOf } from './model';
import { useFaCommand } from './useFaCommand';

/** E48 · RetireTargetBehavior — the Retire drawer, opened from the behavior's own card. */
type Tx = (key: string, options?: Record<string, unknown>) => string;

type Props = {
  open: boolean;
  clientId: string;
  targetId: string | null;
  onClose: () => void;
  onSaved: () => void;
};

export const FaRetireDrawer = ({
  open,
  clientId,
  targetId,
  onClose,
  onSaved,
}: Props) => {
  const t = useTranslate() as unknown as Tx;
  const { message } = AntdApp.useApp();
  const { run, isLoading } = useFaCommand();
  const [reason, setReason] = useState('');
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (open) {
      setReason('');
      setError(null);
    }
  }, [open]);

  const onSave = async () => {
    if (!targetId) return;
    setError(null);
    const res = await run(
      'retireTargetBehavior',
      retireInputOf(clientId, targetId, reason),
      t('fa.errors.FA_VALUE_INVALID'),
    );
    if (res.ok) {
      message.success(t('fa.retire.saved'));
      onSaved();
    } else {
      setError(res.message);
    }
  };

  return (
    <SectionDrawer
      open={open}
      title={t('fa.retire.title')}
      testId="fa-retire"
      saveTestId="fa-retire-submit"
      context={
        <Typography.Text type="secondary">{t('fa.retire.intro')}</Typography.Text>
      }
      saveLabel={t('fa.retire.retire')}
      danger
      saving={isLoading}
      error={error}
      dirty={reason !== ''}
      onSave={() => {
        onSave().catch(() => undefined);
      }}
      onClose={onClose}
    >
      <Typography.Text>{t('fa.retire.reason')}</Typography.Text>
      <Input
        data-testid="fa-retire-reason"
        maxLength={1000}
        onChange={(e) => {
          setReason(e.target.value);
          setError(null);
        }}
        value={reason}
        style={{ marginTop: 4 }}
      />
    </SectionDrawer>
  );
};
